import React, { useEffect, useState, useCallback, Fragment } from "react";
import { Dialog } from "@headlessui/react";
import Calendar from "./Calendar.jsx";
import ConfettiExplosion from "react-confetti-explosion";
import { produce } from "immer";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const tz = "America/Los_Angeles";
const SEVER_API = "https://wooltari-qmlofqf3ja-uc.a.run.app";

export default function Attendance({
  customerId,
  date,
  token,
  available,
  primaryAction,
}) {
  const [init, setInit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [attendances, setAttendances] = useState(null);
  const [modal, setModal] = useState({
    open: false,
    props: {
      title: "",
      subtitle: "",
    },
  });

  useEffect(() => {
    if (customerId) {
      fetchAttendance(date).then((data) => {
        setAttendances(data);
        setInit(true);
      });
    } else {
      setInit(true);
    }
  }, []);

  const fetchAttendance = useCallback(async () => {
    try {
      const data = await fetch(
        // `https://campaigndailycheckin-qmlofqf3ja-uc.a.run.app`,
        "https://checkin-qmlofqf3ja-uc.a.run.app",
        // `${SEVER_API}/campaigns/attendance?date=${date}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
        }
      )
        .then((response) => {
          if (response.status == 204) {
            return null;
          }

          return response.json();
        })
        .catch((error) => {
          console.log(error);
          setHasLoadError(true);
          return null;
        });

      return data;
    } catch (error) {
      console.log(error);
      setHasLoadError(true);
      return null;
    }
  }, [token, date]);

  const handleAttendance = useCallback(
    async (event) => {
      event.preventDefault();
      if (!customerId) {
        window.location.href = `${window.Shopify.routes.root}account/login`;
        return;
      }

      try {
        setIsSubmitting(true);

        if (!token) {
          window.location.href = `${window.Shopify.routes.root}account/login`;
          return;
        }

        window?.mixpanel.track("Check-in", {
          customerId,
          status: "start",
        });

        const response = await fetch(
          // `https://campaigndailycheckin-qmlofqf3ja-uc.a.run.app`,
          "https://checkin-qmlofqf3ja-uc.a.run.app",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `${token}`,
            },
            // body: JSON.stringify({ date: date }),
          }
        );

        if (!response.ok) {
          throw response;
        }

        const data = await response.json();

        setIsSubmitting(false);
        setAttendances(data.attendances);
        setModal({
          open: true,
          confetti: true,
          props: {
            title: "Checked in!",
            subtitle: `You earned ${data.points} points!`,
          },
        });
        window?.mixpanel.track("Check-in", {
          customerId,
          status: "success",
        });
      } catch (error) {
        console.log("error", error);
        window?.mixpanel.track("Check-in", {
          customerId,
          status: "error",
        });
        setIsSubmitting(false);

        if (error.status === 409) {
          setModal({
            open: true,
            confetti: false,
            props: {
              subtitle: "Already Checked In",
            },
          });

          return;
        }

        setModal({
          open: true,
          confetti: false,
          props: {
            title: "Server Error",
            subtitle: "Please try again later",
          },
        });
      }
    },
    [customerId, token]
  );

  const closeModal = useCallback(() => {
    setModal({
      open: false,
      props: {
        title: "",
        body: "",
      },
    });
  });

  const today = dayjs().tz(tz);
  const hasCheckIn = Object.keys(attendances || {}).includes(
    today.format("YYYY-MM-DD")
  );
  const endOfMonth = dayjs.tz(date, tz).endOf("month");
  const isEventFinished =
    today.isAfter(dayjs(endOfMonth).tz(tz)) ||
    !dayjs.tz(date, tz).isSame(today, "month");

  let event_finished_text = "Event has ended";

  const isEventStarted = today.isAfter(dayjs.tz(date, tz).startOf("month"));

  if (isEventFinished && !isEventStarted) {
    event_finished_text = "Event has not started yet";
  }

  const renderButton = useCallback(() => {
    if (!available) {
      return (
        <button
          type="disabled"
          className="inline-flex h-14 lg:h-[70px] lg:text-2xl font-normal shadow-md items-center justify-center text-gray-100 bg-gray-400 py-3 px-8 rounded-lg w-full cursor-not-allowed"
        >
          {isEventFinished ? event_finished_text : "Event has not started yet"}
        </button>
      );
    }

    if (hasLoadError) {
      return (
        <button
          type="disabled"
          className="inline-flex h-14 lg:h-[70px] lg:text-2xl font-normal shadow-md items-center justify-center text-gray-100 bg-gray-400 py-3 px-8 rounded-lg w-full cursor-not-allowed"
        >
          Under Maintenance
        </button>
      );
    }

    if (hasCheckIn) {
      return (
        <button
          type="button"
          disabled={true}
          className="inline-flex h-14 lg:h-[70px] lg:text-2xl font-normal shadow-md items-center justify-center text-gray-100 bg-gray-400 py-3 px-8 rounded-lg w-full"
        >
          Checked In Already
        </button>
      );
    }

    return (
      <button
        disabled={!init || isSubmitting}
        type="button"
        onClick={handleAttendance}
        className="inline-flex h-14 lg:h-[70px] lg:text-2xl font-normal shadow-md items-center justify-center  text-white bg-primary py-3 px-8 rounded-lg w-full
        hover:shadow-lg hover:scale-105 transition duration-300 ease-in-out transform hover:-translate-y-1 hover:bg-primary-dark
        "
      >
        {!init ? (
          <svg
            role="status"
            className="inline w-5 h-5 xl:w-6 xl:h-6 text-gray-200 animate-spin fill-primary"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
        ) : isSubmitting ? (
          <svg
            role="status"
            className="inline w-5 h-5 xl:w-6 xl:h-6 text-gray-200 animate-spin fill-primary"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
        ) : window.Shopify.locale == "ko" ? (
          "출석하기"
        ) : (
          "Check In"
        )}
      </button>
    );
  }, [init, isSubmitting, hasLoadError]);

  return (
    <Fragment>
      <div>
        <div className="">
          <div className="max-w-[450px] mx-auto px-6 relative z-20">
            {renderButton()}
          </div>
          <div className="-mt-[30px]">
            <div className="rounded-xl  bg-white shadow-xl pt-12 relative">
              <span className="inline-block w-[18px] lg:w-[28px] h-[48px] lg:h-[84px] bg-[#23d896] opacity-[27%] absolute -top-[14px] lg:-top-[42px] rounded-t-full rounded-b-full left-4 lg:left-[10%] xl:left-[15%]"></span>
              <span className="inline-block w-[18px] lg:w-[28px] h-[48px] lg:h-[84px] bg-[#23d896] opacity-[27%] absolute -top-[14px] lg:-top-[42px] rounded-t-full rounded-b-full right-4 lg:right-[10%] xl:right-[15%]"></span>
              <div className=" pb-8 px-4">
                <Calendar
                  month={date}
                  attendDateList={attendances ? Object.keys(attendances) : []}
                />
              </div>
              <div className="text-center text-black/90 bg-primary/10 py-4 font-bold lg:py-6 lg:text-xl">
                {window.Shopify.locale === "ko"
                  ? "누적 출석 횟수 : "
                  : "Total Check-ins : "}
                <span className="text-primary font-bold">
                  {attendances ? Object.keys(attendances).length : 0}
                </span>
                {window.Shopify.locale === "ko" ? "회" : ""}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Dialog
        as="div"
        open={modal.open}
        className="fixed inset-0 w-full h-full z-[9990]"
        onClose={() => closeModal()}
      >
        <div
          className="fixed inset-0 z-[9997] bg-black/30"
          aria-hidden="true"
        />
        <div className="fixed inset-0 z-[9998] flex w-screen items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-lg bg-white">
            <div className="py-8 px-4">
              {modal?.confetti && (
                <div className="flex flex-row justify-center w-full">
                  <ConfettiExplosion
                    force={0.4}
                    duration={2200}
                    particleCount={30}
                    width={400}
                    zIndex={9999}
                  />
                </div>
              )}
              {modal?.props?.title && (
                <h3 className="text-center text-primary font-bold text-lg">
                  {modal.props.title}
                </h3>
              )}
              {modal?.props?.subtitle && (
                <p className="text-center text-lg mt-2">
                  {modal.props.subtitle}
                </p>
              )}
            </div>
            <div className="flex flex-col justify-center px-4">
              {primaryAction && (
                <button
                  type="button"
                  onClick={() => (window.location.href = primaryAction.url)}
                  className="text-white bg-primary py-3 px-8 rounded-lg font-semibold w-full "
                >
                  {primaryAction?.label}
                </button>
              )}
              <button
                type="button"
                onClick={() => closeModal()}
                className="text-gray-500 bg-white py-3 px-8 font-semibold w-full "
              >
                Close
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </Fragment>
  );
}
